import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAdminCanonicalListing,
  getAdminCanonicalListings,
  patchAdminCanonicalMailingPublish,
  rebuildAdminCanonicalListing,
  updateAdminCanonicalStatus,
} from "../../../services/midpointApi";
import { formatCanonicalPriceRange } from "../../../utils/canonicalListings";
import { CardLoading } from "../intelligence/CardState";
import CanonicalPropertyMediaSection from "./CanonicalPropertyMediaSection";
import CanonicalPropertyHistorySection from "./CanonicalPropertyHistorySection";
import AdminStatePanel from "./layout/AdminStatePanel";
import CasaMapaAdminListingAddress from "../../casamapa/admin/CasaMapaAdminListingAddress";
import "../../../styles/casamapaListingAdminDetail.css";

const MAILING_ADDRESS_COPY = {
  "realEstate.casaMapa.admin.listings.detail.address.title": "Mailing address",
  "realEstate.casaMapa.admin.listings.detail.address.hiddenPublic": "Hidden on public site",
  "realEstate.casaMapa.admin.listings.detail.address.publishedPublic": "Published on public site",
  "realEstate.casaMapa.admin.listings.detail.address.source": "Source",
  "realEstate.casaMapa.admin.listings.detail.address.notSnapped":
    "No official snap yet. This line stays off the public site.",
  "realEstate.casaMapa.admin.listings.detail.address.class": "Class {class}",
  "realEstate.casaMapa.admin.listings.detail.address.snap": "{meters} m from pin",
  "realEstate.casaMapa.admin.listings.detail.address.publishStreet": "Publish street publicly",
  "realEstate.casaMapa.admin.listings.detail.address.unpublishStreet": "Hide street publicly",
  "realEstate.casaMapa.admin.listings.detail.address.publishHint":
    "Only official snaps can be published. CasaMapa stays locality-only until approved.",
  "realEstate.casaMapa.admin.listings.detail.address.publishBlocked":
    "Needs an official mailing snap before publish.",
  "realEstate.casaMapa.admin.listings.detail.address.publishSaving": "Saving…",
  "realEstate.casaMapa.admin.listings.detail.address.publishError": "Could not update publish state.",
};

function mailingAddressT(key, vars = {}) {
  const template = MAILING_ADDRESS_COPY[key] || key;
  return String(template).replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] != null ? String(vars[name]) : `{${name}}`
  );
}

const CanonicalListingsPage = ({ onClose }) => {
  const [listings, setListings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [togglingPublicStreet, setTogglingPublicStreet] = useState(false);

  const loadListings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getAdminCanonicalListings({ limit: 200, includeInactive: "true" });
      setListings(response.data || []);
    } catch (loadError) {
      setListings([]);
      setError(loadError.message || "Unable to load canonical listings.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }

    try {
      setDetailLoading(true);
      setActionError("");
      const response = await getAdminCanonicalListing(id);
      setDetail(response.data || null);
    } catch (loadError) {
      setDetail(null);
      setActionError(loadError.message || "Unable to load canonical listing detail.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const runAction = async (action) => {
    if (!selectedId) {
      return;
    }

    try {
      setActionError("");
      if (action === "rebuild") {
        await rebuildAdminCanonicalListing(selectedId);
      } else if (action === "hide") {
        await updateAdminCanonicalStatus(selectedId, { canonicalStatus: "HIDDEN" });
      } else if (action === "confirm") {
        await updateAdminCanonicalStatus(selectedId, {
          reviewStatus: "ADMIN_CONFIRMED",
          canonicalStatus: "ACTIVE",
        });
      } else if (action === "activate") {
        await updateAdminCanonicalStatus(selectedId, { canonicalStatus: "ACTIVE" });
      }

      await loadListings();
      await loadDetail(selectedId);
    } catch (actionErr) {
      setActionError(actionErr.message || "Action failed.");
    }
  };

  const handleTogglePublicStreet = async (nextApproved) => {
    if (!selectedId) return;
    setTogglingPublicStreet(true);
    setActionError("");
    try {
      const response = await patchAdminCanonicalMailingPublish(selectedId, nextApproved);
      setDetail((current) =>
        current
          ? {
              ...current,
              mailingAddress: response.mailingAddress || current.mailingAddress,
            }
          : current
      );
    } catch (actionErr) {
      setActionError(actionErr.message || "Could not update mailing publish.");
      throw actionErr;
    } finally {
      setTogglingPublicStreet(false);
    }
  };

  const filteredListings = useMemo(() => {
    let list = listings;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (l) =>
          l.title?.toLowerCase().includes(q) ||
          l.city?.toLowerCase().includes(q) ||
          l.neighborhood?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "all") {
      list = list.filter((l) => l.canonicalStatus === statusFilter);
    }

    return list;
  }, [listings, search, statusFilter]);

  const kpis = useMemo(() => {
    const total = listings.length;
    const active = listings.filter((l) => l.canonicalStatus === "ACTIVE").length;
    const hidden = listings.filter((l) => l.canonicalStatus === "HIDDEN").length;
    const conflicts = listings.filter((l) => l.reviewStatus === "CONFLICT").length;
    const pending = listings.filter((l) => l.reviewStatus === "PENDING_REVIEW").length;

    return { total, active, hidden, conflicts, pending };
  }, [listings]);

  return (
    <div className="admin-dashboard admin-canonical-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Canonical Listings</h1>
          <p className="admin-dashboard__subtitle">
            Aggregated map listings from broker source records
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadListings}
            disabled={loading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!loading && !error && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Listings</span>
            <span className="admin-dash__kpi-value">{kpis.total}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Active</span>
            <span className="admin-dash__kpi-value admin-dash__kpi-value--green">
              {kpis.active}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Hidden</span>
            <span className="admin-dash__kpi-value">{kpis.hidden}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Conflicts</span>
            <span
              className={`admin-dash__kpi-value${kpis.conflicts > 0 ? " admin-dash__kpi-value--amber" : ""}`}
            >
              {kpis.conflicts}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Pending Review</span>
            <span
              className={`admin-dash__kpi-value${kpis.pending > 0 ? " admin-dash__kpi-value--amber" : ""}`}
            >
              {kpis.pending}
            </span>
          </div>
        </section>
      )}

      {actionError ? (
        <div className="admin-re-toast admin-re-toast--error" role="alert">
          {actionError}
        </div>
      ) : null}

      {loading ? <AdminStatePanel variant="loading" message="Loading listings…" /> : null}

      {!loading && error ? (
        <AdminStatePanel variant="error" message={error} onRetry={loadListings} />
      ) : null}

      {!loading && !error && (
        <div className="admin-canonical-dash__layout">
          <section className="admin-dash__card admin-canonical-dash__list-card">
            <div className="admin-canonical-dash__toolbar">
              <input
                type="search"
                className="admin-source-controls-dash__search"
                placeholder="Search listings…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className="admin-source-controls-dash__select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="HIDDEN">Hidden</option>
                <option value="INACTIVE">Inactive</option>
              </select>
              <span className="admin-canonical-dash__count">
                {filteredListings.length} listing{filteredListings.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="admin-canonical-dash__list">
              {filteredListings.map((listing) => (
                <button
                  key={listing.canonicalListingId}
                  type="button"
                  className={`admin-canonical-dash__row${
                    selectedId === listing.canonicalListingId ? " is-selected" : ""
                  }`}
                  onClick={() => setSelectedId(listing.canonicalListingId)}
                >
                  <div className="admin-canonical-dash__row-main">
                    <strong>{listing.title}</strong>
                    <span className="muted">
                      {[listing.neighborhood, listing.city].filter(Boolean).join(", ") || "—"}
                    </span>
                  </div>
                  <div className="admin-canonical-dash__row-meta">
                    <span>{formatCanonicalPriceRange(listing)}</span>
                    <span className="admin-canonical-dash__badge">
                      {listing.sourceCount || 0} source{(listing.sourceCount || 0) === 1 ? "" : "s"}
                    </span>
                    <span
                      className={`admin-canonical-dash__status-pill${
                        listing.canonicalStatus === "ACTIVE"
                          ? " admin-canonical-dash__status-pill--active"
                          : listing.canonicalStatus === "HIDDEN"
                            ? " admin-canonical-dash__status-pill--hidden"
                            : ""
                      }`}
                    >
                      {listing.canonicalStatus}
                    </span>
                  </div>
                </button>
              ))}
              {!filteredListings.length && (
                <p className="muted admin-canonical-dash__empty">
                  No listings match the current filters.
                </p>
              )}
            </div>
          </section>

          <section className="admin-dash__card admin-canonical-dash__detail-card">
            {!selectedId ? (
              <div className="admin-canonical-dash__detail-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                <p className="muted">Select a listing to view details</p>
              </div>
            ) : null}

            {selectedId && detailLoading ? <CardLoading /> : null}

            {selectedId && !detailLoading && detail ? (
              <div className="admin-canonical-dash__detail-content">
                <header className="admin-canonical-dash__detail-header">
                  <h2 className="admin-dash__card-title">{detail.title}</h2>
                  <span className="admin-canonical-dash__detail-price">
                    {formatCanonicalPriceRange(detail)}
                  </span>
                </header>

                <div className="admin-canonical-dash__detail-meta">
                  <div>
                    <span className="admin-dash__kpi-label">Quality</span>
                    <strong>{detail.qualityScore ?? "—"}</strong>
                  </div>
                  <div>
                    <span className="admin-dash__kpi-label">Location</span>
                    <strong>{detail.locationQuality || "—"}</strong>
                  </div>
                  <div>
                    <span className="admin-dash__kpi-label">Review</span>
                    <strong>{detail.reviewStatus}</strong>
                  </div>
                  <div>
                    <span className="admin-dash__kpi-label">Status</span>
                    <strong>{detail.canonicalStatus}</strong>
                  </div>
                </div>

                <p className="muted" style={{ marginTop: "0.75rem" }}>
                  {[detail.neighborhood, detail.city].filter(Boolean).join(", ") || "—"}
                </p>
                <CasaMapaAdminListingAddress
                  mailingAddress={detail.mailingAddress}
                  t={mailingAddressT}
                  onTogglePublicStreet={handleTogglePublicStreet}
                  togglingPublicStreet={togglingPublicStreet}
                />

                {detail.reviewStatus === "CONFLICT" ? (
                  <p className="admin-csv-import-dash__error">
                    Field conflicts detected across linked broker sources.
                  </p>
                ) : null}

                <div className="admin-canonical-dash__actions">
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                    onClick={() => runAction("rebuild")}
                  >
                    Rebuild
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    onClick={() => runAction("confirm")}
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    onClick={() => runAction("activate")}
                  >
                    Activate
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-canonical-dash__action-danger"
                    onClick={() => runAction("hide")}
                  >
                    Hide
                  </button>
                </div>

                <div className="admin-canonical-dash__section">
                  <h3>Linked Broker Listings</h3>
                  {(detail.linkedBrokerListings || []).length ? (
                    <ul className="admin-canonical-dash__linked-list">
                      {detail.linkedBrokerListings.map((listing) => (
                        <li key={listing.propertyId}>
                          <strong>{listing.displaySource || listing.source}</strong>
                          <span>{listing.title}</span>
                          {listing.detailUrl ? (
                            <a href={listing.detailUrl} target="_blank" rel="noopener noreferrer">
                              Open source
                            </a>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">No linked broker listings.</p>
                  )}
                </div>

                {(detail.duplicateHistory || []).length ? (
                  <div className="admin-canonical-dash__section">
                    <h3>Duplicate History</h3>
                    <ul className="admin-canonical-dash__linked-list">
                      {detail.duplicateHistory.map((entry) => (
                        <li key={entry.duplicateCandidateId}>
                          <span>Score {entry.matchScore} · {entry.status}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <CanonicalPropertyMediaSection
                  canonicalPropertyId={detail.canonicalListingId || selectedId}
                />
                <CanonicalPropertyHistorySection
                  canonicalPropertyId={detail.canonicalListingId || selectedId}
                />
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
};

export default CanonicalListingsPage;
